/**
 * Map integration & search helpers.
 *
 * Everything the full-screen map needs to turn the existing published
 * property / parking feeds into a single searchable, filterable, clusterable
 * set of pins. No database changes — this layer is purely derived.
 */

import type { Property, Purpose, PropertyType } from "@/lib/mock-data";
import type { ParkingListing, ParkingType, VehicleType } from "@/lib/parking-mock-data";
import { parkingPriceLabel } from "@/lib/parking";

/* ------------------------------------------------------------------ *
 * Unified map item
 * ------------------------------------------------------------------ */

export interface MapItem {
  id: string;
  kind: "property" | "parking";
  purpose: Purpose;
  title: string;
  price: number;
  priceLabel: string;
  typeLabel: string;
  propertyType?: PropertyType;
  image: string;
  location: string;
  city: string;
  distanceKm: number;
  amenities: string[];
  parkingType?: ParkingType;
  vehicleTypes?: VehicleType[];
  /** faux-map viewport position (%) */
  x: number;
  y: number;
  /** Real coordinates when the listing has them (live data). */
  lat?: number;
  lng?: number;
  createdAt?: string;
  /** lowercased haystack: city, area, locality, pincode, landmark */
  search: string;
  property?: Property;
  parking?: ParkingListing;
}

/** Rough viewport → km scale for the faux map (full width ≈ 12 km). */
const VIEWPORT_KM = 12;

function viewportDistanceKm(x: number, y: number) {
  const dx = ((x - 50) / 100) * VIEWPORT_KM;
  const dy = ((y - 50) / 100) * VIEWPORT_KM;
  return Math.round(Math.sqrt(dx * dx + dy * dy) * 10) / 10;
}

export function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(s)) * 10) / 10;
}

function haystack(parts: (string | null | undefined)[]) {
  return parts.filter(Boolean).join(" ").toLowerCase();
}

export function propertyToItem(p: Property): MapItem {
  return {
    id: p.id,
    kind: "property",
    purpose: p.purpose,
    title: p.title,
    price: p.price,
    priceLabel: p.priceLabel,
    typeLabel: p.type.replace(/_/g, " "),
    propertyType: p.type,
    image: p.image,
    location: p.location,
    city: p.location.split(",").pop()?.trim() ?? "",
    distanceKm: p.distanceKm || viewportDistanceKm(p.x, p.y),
    amenities: p.highlights ?? [],
    x: p.x,
    y: p.y,
    lat: p.lat,
    lng: p.lng,
    createdAt: p.createdAt,
    search: haystack([p.title, p.location, p.type, ...(p.highlights ?? [])]),
    property: p,
  };
}

export function parkingToItem(p: ParkingListing): MapItem {
  const amenities = [
    p.evCharging ? "EV Charging" : null,
    p.cctv ? "CCTV" : null,
    p.security ? "Security" : null,
    p.lighting ? "Lighting" : null,
    p.open24h ? "24-hour access" : null,
  ].filter(Boolean) as string[];
  return {
    id: p.id,
    kind: "parking",
    purpose: "parking",
    title: p.title,
    price: p.priceHourly ?? p.priceDaily ?? p.priceMonthly ?? 0,
    priceLabel: parkingPriceLabel(p),
    typeLabel: p.parkingType.replace(/_/g, " "),
    image: p.image,
    location: [p.address, p.city].filter(Boolean).join(", "),
    city: p.city,
    distanceKm: p.distanceKm || viewportDistanceKm(p.x, p.y),
    amenities,
    parkingType: p.parkingType,
    vehicleTypes: p.vehicleTypes,
    x: p.x,
    y: p.y,
    lat: p.lat,
    lng: p.lng,
    createdAt: p.createdAt,
    search: haystack([p.title, p.address, p.city, p.providerName, p.parkingType, ...amenities]),
    parking: p,
  };
}

/* ------------------------------------------------------------------ *
 * Search — city, area, locality, pincode, landmark
 * ------------------------------------------------------------------ */

export function matchesQuery(item: MapItem, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return q
    .split(/[\s,]+/)
    .filter(Boolean)
    .every((token) => item.search.includes(token));
}

export interface SearchSuggestion {
  label: string;
  kind: "City" | "Area" | "Locality" | "Pincode" | "Landmark";
  count: number;
}

const PINCODE_RE = /\b\d{6}\b/g;

/** Build type-ahead suggestions from the live feed. */
export function buildSuggestions(items: MapItem[], query: string): SearchSuggestion[] {
  const q = query.trim().toLowerCase();
  const bucket = new Map<string, SearchSuggestion>();

  const add = (raw: string | undefined, kind: SearchSuggestion["kind"]) => {
    const label = (raw ?? "").trim();
    if (label.length < 2) return;
    const key = `${kind}:${label.toLowerCase()}`;
    const found = bucket.get(key);
    if (found) found.count += 1;
    else bucket.set(key, { label, kind, count: 1 });
  };

  for (const item of items) {
    const parts = item.location.split(",").map((s) => s.trim());
    add(item.city, "City");
    if (parts.length > 1) add(parts[0], "Area");
    if (parts.length > 2) add(parts[1], "Locality");
    for (const pin of item.location.match(PINCODE_RE) ?? []) add(pin, "Pincode");
    for (const landmark of item.amenities) add(landmark, "Landmark");
  }

  return [...bucket.values()]
    .filter((s) => !q || s.label.toLowerCase().includes(q))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 8);
}

/* ------------------------------------------------------------------ *
 * Clustering
 * ------------------------------------------------------------------ */

export interface Cluster {
  id: string;
  x: number;
  y: number;
  items: MapItem[];
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 5;
/** Clusters fully expand at this zoom and above. */
export const EXPAND_ZOOM = 4;

/**
 * Grid clustering in viewport space. Cells shrink as zoom increases, so
 * clusters naturally break apart when the user zooms in.
 */
export function clusterItems(items: MapItem[], zoom: number): Cluster[] {
  if (zoom >= EXPAND_ZOOM) {
    return items.map((i) => ({ id: i.id, x: i.x, y: i.y, items: [i] }));
  }
  const cell = 26 / zoom;
  const cells = new Map<string, MapItem[]>();
  for (const item of items) {
    const key = `${Math.floor(item.x / cell)}:${Math.floor(item.y / cell)}`;
    const list = cells.get(key);
    if (list) list.push(item);
    else cells.set(key, [item]);
  }
  return [...cells.entries()].map(([key, group]) => ({
    id: `c${key}`,
    x: group.reduce((s, i) => s + i.x, 0) / group.length,
    y: group.reduce((s, i) => s + i.y, 0) / group.length,
    items: group,
  }));
}

/** Zoom scales positions around the map centre. */
export function projectWithZoom(x: number, y: number, zoom: number) {
  return { x: 50 + (x - 50) * zoom, y: 50 + (y - 50) * zoom };
}
